import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { OverlayRef, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

export interface SelectItem {
    value: string | number;
    label: string;
    [key: string]: any;
}

@Component({
    selector: 'app-select-list-v2',
    templateUrl: './select-list-v2.component.html',
    styleUrls: ['./select-list-v2.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectListV2Component),
            multi: true,
        },
    ],
})
export class SelectListV2Component implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() label: string;

    @Input() placeholder = '';

    @Input() items: SelectItem[] = [];

    @Input() disabled = false;

    @Input() required = false;

    @Input() errorMessage = '';

    @Input() hasError = false;

    @Output() selectionChange = new EventEmitter<SelectItem>();

    @ViewChild('selectContainer') selectContainer: ElementRef;

    @ViewChild(CdkPortal) contentTemplate: CdkPortal;

    selectedItem: SelectItem | null = null;

    isOpen = false;

    protected overlayRef: OverlayRef;

    private backdropSubscription: Subscription;

    // ControlValueAccessor implementation
    private onChange = (_value: any): void => {
        // This is intentionally left empty and will be overridden by registerOnChange
    };

    private onTouched = (): void => {
        // This is intentionally left empty and will be overridden by registerOnTouched
    };

    constructor(
        private elementRef: ElementRef,
        private translate: TranslateService,
        protected overlay: Overlay,
    ) {}

    ngOnInit(): void {
        // Component initialization logic can be added here if needed
    }

    ngOnDestroy(): void {
        if (this.backdropSubscription) {
            this.backdropSubscription.unsubscribe();
        }
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.hideDropdown();
        }
    }

    toggleDropdown(event: Event): void {
        event.stopPropagation();
        if (!this.disabled) {
            if (this.isOpen) {
                this.hideDropdown();
            } else {
                this.showDropdown();
            }
            this.onTouched();
        }
    }

    showDropdown(): void {
        if (this.isOpen) {
            return;
        }

        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.overlayRef.attach(this.contentTemplate);
        this.syncWidth();
        this.backdropSubscription = this.overlayRef.backdropClick().subscribe(() => {
            this.hideDropdown();
        });
        this.isOpen = true;

        // Enable wheel scrolling by attaching a wheel event listener to the dropdown
        setTimeout(() => {
            const panel = document.querySelector('.select-list-v2-overlay .dropdown-panel');
            if (panel) {
                panel.addEventListener('wheel', (e: WheelEvent) => {
                    e.stopPropagation();
                });
            }
        }, 50);
    }

    hideDropdown(): void {
        if (this.overlayRef) {
            this.overlayRef.detach();
        }
        if (this.backdropSubscription) {
            this.backdropSubscription.unsubscribe();
        }
        this.isOpen = false;
    }

    syncWidth(): void {
        if (!this.overlayRef || !this.selectContainer) return;

        const refRect = this.selectContainer.nativeElement.getBoundingClientRect();
        this.overlayRef.updateSize({ width: refRect.width });
    }

    protected getOverlayConfig(): OverlayConfig {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.selectContainer.nativeElement)
            .withPush(true)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 4,
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -4,
                },
            ]);

        const scrollStrategy = this.overlay.scrollStrategies.noop();

        return new OverlayConfig({
            positionStrategy,
            scrollStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: ['select-list-v2-overlay', 'scrollable-dropdown'],
            maxHeight: '300px',
        });
    }

    selectItem(item: SelectItem, event: Event): void {
        event.stopPropagation();
        this.selectedItem = item;
        this.hideDropdown();
        this.onChange(item.value);
        this.selectionChange.emit(item);
    }

    isSelected(item: SelectItem): boolean {
        return this.selectedItem?.value === item.value;
    }

    // ControlValueAccessor methods
    writeValue(value: any): void {
        if (value !== undefined && value !== null) {
            this.selectedItem = this.items.find((item) => item.value === value) || null;
        } else {
            this.selectedItem = null;
        }
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
